import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="w-full px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-xl text-foreground-900">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '诱人的飞行',
    },
    url: `https://airluxx.github.io/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://airluxx.github.io/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-20 pb-12">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mt-6 md:mt-8">
              <span className="inline-block px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-800">
                {article.categoryName}
              </span>
              <h1 className="mt-3 font-heading text-xl md:text-3xl font-semibold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-7 h-7 rounded-full object-cover"
                  />
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>
            </div>

            <div className="mt-6 md:mt-8 rounded-lg overflow-hidden aspect-[16/8]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="mt-8 md:mt-10 prose prose-sm md:prose-base max-w-none"
              dangerouslySetInnerHTML={{ __html: convertToProse(article.contentHtml) }}
            />

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-4">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">旅行作家 &middot; 航空评测师</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 mt-12">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="mt-1 text-xs text-foreground-500">{ra.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function convertToProse(html: string): string {
  return html
    .replace(/<p>/g, '<p class="text-foreground-700 leading-relaxed mb-4">')
    .replace(/<h2>/g, '<h2 class="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3">')
    .replace(/<h3>/g, '<h3 class="font-heading text-base md:text-lg font-semibold text-foreground-900 mt-6 mb-2">');
}